import React, { useMemo, useRef, useEffect, useState } from 'react';
import { useRouteMatch, useHistory, Link } from 'react-router-dom';
import { Button, Input, Space, Popconfirm } from 'antd';
import Highlighter from 'react-highlight-words';
import { SearchOutlined } from '@ant-design/icons';
import PageTitleActions from '@iso/containers/Dashboard/PageTitleActions';
import AppLayout from '../../Integration/NotasFiscais/AppLayout';
import FormWindow from '@iso/containers/Dashboard/FormWindow';
import EditReason from './EditReason';
import TableWrapper from '../../Tables/AntTables/AntTables.styles';
import HelperText from '@iso/components/utility/helper-text';
import Loader from '@iso/components/utility/loader';
import CardWrapper, { Box } from '../Messaging/Campaigns.style';
import { TypeMovementTag, StatusTag } from './Stock.style';
import notification from '@iso/components/Notification';

import apiReasons from '@iso/services/repository/reasons';
import { canAccessModule, displayDateTime } from '@iso/lib/helpers/utility';

export default function () {

    let history = useHistory();
    const match = useRouteMatch();

    const searchInput = useRef(null);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [initialReason, setInitialReason] = useState(false);
    const [reasons, setReasons] = useState([]);
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        getReasons()
    }, []);

    const getReasons = () => {
        apiReasons.getAll().then(res => {
            setReasons(res.data);
            setInitialReason(true)
        })
    };

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex, collum) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div
                style={{
                    padding: 8,
                }}
                onKeyDown={(e) => e.stopPropagation()}
            >
                <Input
                    ref={searchInput}
                    placeholder={`Buscar ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, collum)}
                    style={{
                        marginBottom: 8,
                        display: 'block',
                    }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, collum)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Buscar
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Limpar
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        Fechar
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined
                title={"Pesquisar pelo " + dataIndex + " da Campanha"}
                style={{
                    color: '#000000',
                }}
            />
        ),
        onFilter: (value, record) => {
            return record[collum]?.toString().toLowerCase().includes(value.toLowerCase())
        },
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === collum ? (
                <Highlighter
                    highlightStyle={{
                        backgroundColor: '#ffc069',
                        padding: 0,
                    }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns = useMemo(() => [
        {
            title: 'Tipo',
            dataIndex: 'type',
            key: 'type',
            width: '10%',
            align: 'center',
            filters: [
                {
                    text: 'Entrada',
                    value: 'Entrada',
                },
                {
                    text: 'Saída',
                    value: 'Saida',
                },
            ],
            onFilter: (value, record) => record.type === value,
            filterSearch: true,
            render: (text) => <span><TypeMovementTag className={text}>{text}</TypeMovementTag></span>
        },
        {
            title: 'Motivo',
            dataIndex: 'name',
            key: 'name',
            width: '35%',
            align: 'center',
            render: (text) => <span>{text}</span>,
            ...getColumnSearchProps('Motivo', 'name')
        },
        {
            title: 'Usuário',
            dataIndex: 'usuario',
            key: 'usuario',
            width: '15%',
            align: 'center',
            render: (text) => <span>{text}</span>,
            ...getColumnSearchProps('Usuário', 'usuario')
        },
        {
            title: 'Horário de criação',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: '15%',
            align: 'center',
            render: (text) => <span>{displayDateTime(text)}</span>
        },
        {
            title: 'Status',
            dataIndex: 'situation',
            key: 'situation',
            width: '15%',
            align: 'center',
            render: (text) => <StatusTag className={text}>{text}</StatusTag>
        },
        {
            title: '',
            dataIndex: 'view',
            key: 'view',
            width: '10%',
            render: (_, reason) => <>
                <a href={`${match.path}/${reason._id}`} onClick={(event) => { event.preventDefault(); setEditingId(reason._id); }}>
                    <Button color="primary" className="peopleViewBtn">
                        Editar
                    </Button>
                </a>
                <Popconfirm title="Deseja realmente deletar?" onConfirm={() => {
                    apiReasons.softDeleteReason(reason._id).then(res => {
                        console.log(res);
                        if (res.data === 'ok') {
                            notification('success', 'Motivo deletado!');
                            getReasons();
                        }
                        else
                            notification('error', 'Houve uma falha ao deletar motivo');
                    })
                }}>
                    <Button className="peopleDltBtn" onClick={() => { }}>
                        <i className="ion-android-delete" />
                    </Button>
                </Popconfirm>
            </>
        }
    ]);

    return (
        (canAccessModule("Estoque")) ?
            <AppLayout history={history} match={match} title="Motivos de movimentação">
                <PageTitleActions primary={{ key: 'create', label: 'Adicionar', onClick: () => setEditingId('0') }} />
                <Box style={{ border: 'none', boxShadow: 'none' }}>
                                        {!initialReason ? (
                        <Loader />
                    ) : (
                        <CardWrapper>
                            {reasons !== undefined && reasons.length === 0 ? (
                                <HelperText text="Sem motivos até o momento" />
                            ) : (
                                <div>
                                    <TableWrapper
                                        rowKey={"_id"}
                                        dataSource={reasons}
                                        columns={columns}
                                        pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                                        className="campanhasListTable"
                                    />
                                </div>
                            )}
                        </CardWrapper>
                    )}
                </Box>
                <FormWindow
                    open={editingId !== null}
                    title={editingId === '0' ? 'Cadastro de Motivo de movimentação' : 'Edição de Motivo de movimentação'}
                    onClose={() => setEditingId(null)}
                >
                    {({ footerEl }) =>
                        editingId !== null ? (
                            <EditReason
                                reasonId={editingId}
                                inModal
                                footerEl={footerEl}
                                onCancel={() => setEditingId(null)}
                                onSaved={() => {
                                    setEditingId(null);
                                    getReasons();
                                }}
                            />
                        ) : null
                    }
                </FormWindow>
            </AppLayout>
            : <></>
    );
}